import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LinkingOptions, NavigationContainer } from '@react-navigation/native';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ja } from 'date-fns/locale';
import { useRef } from 'react';
import { Dimensions, Text, View } from 'react-native';
import { ReactNotifications } from 'react-notifications-component';
import 'react-notifications-component/dist/theme.css';
import { RecoilRoot } from 'recoil';
import { TwilioVideoProvider } from 'src/components/twilio';
import { isMobile } from 'src/components/twilio/utils';
import { AuthProvider, CallProvider, PushProvider } from 'src/hooks';
import { navigationRef } from 'src/hooks/navigation';
import { NavigationProvider } from 'src/navigation';
import { RootStackParamList } from 'src/navigation/types';
import { gaPageView } from 'src/utils/ga';
import { StripePublishableKey } from './aws-config';

const MAX_WIDTH = 720;
const LINKING: LinkingOptions<RootStackParamList> = {
  prefixes: [
    'https://localhost:3000',
    'https://dev.d2hrwopyzvn1ns.amplifyapp.com',
    'https://app.choku.me',
  ],
  config: {
    screens: {
      Main: {
        screens: {
          Search: '/',
          PreOfferChatList: 'chats',
          CallHistoryList: 'calls',
          Offers: 'my-services',
          MyPage: 'my-page',
        },
      },
      Auth: 'auth',
      SignIn: 'auth/sign-in',
      PasswordReset: 'auth/password-reset',
      PasswordResetConfirm: 'auth/password-reset/confirm',
      SignUp: 'auth/sign-up',
      SignUpConfirm: 'auth/sign-up/confirm',
      Offer: 'services/:offerId',
      // LikedService: '',
      // BlockedUsers: '',
      // ReviewByUserAsUser: '',
      // ReviewByUserAsServiceOwner: '',
      MyProfile: 'my-page/profile',
      // CustomSelectionList: '',
      Notifications: 'my-page/notifications',
      Notification: 'my-page/notifications/:id',
      // ReviewByOffer: '',
      // ReviewByOfferToUser: '',
      User: 'users/:id',
      MyAccount: 'my-page/account',
      MyAccountEmail: 'my-page/account/email',
      MyAccountPassword: 'my-page/account/password',
      HomeFilter: 'search/filter',
      OfferEdit: 'my-services/:offerId',
      // CustomTextEdit: '',
      CallRequest: 'offer/:offerId',
      CallHistory: 'calls/:callId',
      Call: 'call',
      CallChat: 'call/chat',
      Review: 'review/:callId',
      SalesHistory: 'my-page/sales',
      Withdrawal: 'my-page/withdrawal',
      WithdrawalRequest: 'my-page/withdrawal/request',
      WithdrawalDescription: 'my-page/withdrawal/description',
      Balance: 'my-page/balance',
      PreOfferChat: 'chats/:preOfferId',
      Owner: 'owners/:id',
      MyAddress: 'my-page/profile/address',
      Identification: 'my-page/profile/identification',
      // CustomImageCrop: ''
      PrivacyPolicy: 'privacy-policy',
      Terms: 'terms',
    },
  },
  // getStateFromPath: (path, options) => {
  //   // Return a state object here
  //   // You can also reuse the default logic by importing `getStateFromPath` from `@react-navigation/native`
  //   console.log("getStateFromPath...", path);
  //   return getStateFromPath(path, options);
  // },
  // getPathFromState(state, config) {
  //   // Return a path string here
  //   // You can also reuse the default logic by importing `getPathFromState` from `@react-navigation/native`
  //   console.log("getPathFromState...", state);
  //   return getPathFromState(state, config);
  // },
};

const stripePromise = loadStripe(StripePublishableKey);

const App = () => {
  const lastPathRef = useRef<string>('');

  return (
    <View
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        // height: "100vh",
      }}
    >
      <RecoilRoot>
        <LocalizationProvider
          locale={ja}
          dateAdapter={AdapterDateFns}
        >
          <Elements stripe={stripePromise}>
            <View
              style={{
                ...(!isMobile
                  ? {
                      maxWidth: MAX_WIDTH,
                      minWidth:
                        Dimensions.get('window').width > MAX_WIDTH
                          ? MAX_WIDTH
                          : Dimensions.get('window').width,
                      marginLeft: 'auto',
                      marginRight: 'auto',
                    }
                  : {}),
                flex: 1,
              }}
            >
              <AuthProvider>
                <PushProvider>
                  <TwilioVideoProvider>
                    <CallProvider>
                      <ReactNotifications />
                      <NavigationContainer
                        ref={navigationRef}
                        linking={LINKING}
                        fallback={<Text>Loading...</Text>}
                        onReady={() => {
                          lastPathRef.current =
                            window.location.pathname + window.location.search;
                          gaPageView();
                        }}
                        onStateChange={() => {
                          const path = window.location.pathname + window.location.search;
                          if (path !== lastPathRef.current) {
                            lastPathRef.current = path;
                            gaPageView();
                          }
                        }}
                      >
                        <NavigationProvider />
                      </NavigationContainer>
                    </CallProvider>
                  </TwilioVideoProvider>
                </PushProvider>
              </AuthProvider>
            </View>
          </Elements>
        </LocalizationProvider>
      </RecoilRoot>
    </View>
  );
};

export default App;
